import isEqual from 'lodash/isEqual'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDrop } from 'react-dnd'
import type { Point } from 'types/screen'
import * as ItemTypes from '@disco/constants/ItemTypes'
import { SortableContext } from 'contexts/sortable'
import { moveItems2 } from '@disco/utils/immutable'
import MediaItem from 'components/ui/media-item'
import PaneItem from 'components/ui/pane-item'
import Skeleton from 'components/ui/skeleton'
import SortableItem from './SortableItem'
const FILE_PREVIEW = (
  <PaneItem
    content={<MediaItem title={<Skeleton />} caption={<Skeleton />} />}
  />
)
export const NEW_SORTABLE_ITEM_KEY = '-1'

const defaultKeyExtractor = (item: any, index: number): string => {
  return String(item.id || index)
}

type Props = {
  accept: (typeof ItemTypes)[keyof typeof ItemTypes][]
  children?: (items: React.ReactNode) => React.ReactNode
  id: string
  items: any[]
  keyExtractor?: (item: any, index: number) => string
  renderItem: (item: any, index: number, items: any[]) => React.ReactNode
  hidePreview?: boolean
  onDrop?: (dropResult: Record<string, any>) => void
  onOrderUpdate?: (order: string[], item?: any) => void
  thresholdHandler?: (
    draggableItem: any,
    dragoverItem: any,
    dragInfo: {
      pointer: Point
      entryPoint: Point | null | undefined
      targetClientRect: ClientRect
    },
  ) => boolean
}

/**
 * A sortable container component that allows drag-and-drop reordering of items
 * @param accept - Array of item types that can be dropped into this sortable container
 * @param children - Optional render function that receives the sorted items
 * @param id - Unique identifier for this sortable container
 * @param items - Array of items to be sorted
 * @param keyExtractor - Function to extract unique keys from items (defaults to using id or index)
 * @param renderItem - Function to render each item
 * @param onOrderUpdate - Callback called when the order of items changes
 * @param hidePreview - Whether to hide the drag preview
 * @param onDrop - Callback called when an item is dropped
 * @param thresholdHandler - Optional function to handle drag threshold logic
 * @returns A sortable container with drag-and-drop functionality
 */
const Sortable = ({
  accept,
  children,
  id,
  items,
  keyExtractor = defaultKeyExtractor,
  renderItem,
  onOrderUpdate,
  hidePreview,
  onDrop,
  thresholdHandler,
}: Props) => {
  const containerRef = useRef<HTMLDivElement | null | undefined>(null)
  const overIndex = useRef<number | null | undefined>(null)
  const [order, setOrder] = useState<string[]>([])
  const itemsMap = useMemo(() => {
    let map = new Map()

    for (let i = 0; i < items.length; i++) {
      const item = items[i]
      const key = keyExtractor(item, i)
      map.set(key, item)
    }

    return map
  }, [items])
  const itemsKeys = useMemo(() => [...itemsMap.keys()], [itemsMap])
  useEffect(() => {
    if (
      !isEqual([...order].sort(), [...itemsKeys].sort()) &&
      !isEqual(order, itemsKeys)
    ) {
      setOrder(itemsKeys)
    }
  }, [itemsMap])
  const [{ draggableItem, isOver, didDrop }, drop] = useDrop(
    () => ({
      accept,
      collect: monitor => ({
        draggableItem: monitor.getItem(),
        isOver: monitor.canDrop() && monitor.isOver(),
        didDrop: monitor.didDrop(),
      }),
      drop: (item: any, monitor) => {
        const dropResult = {
          ...item,
          ...monitor.getDropResult<any>(),
          index: overIndex.current,
          order,
          sortableId: id,
        }

        if (onDrop) {
          onDrop(dropResult)
        }

        return dropResult
      },
    }),
    [accept, order],
  )
  useEffect(() => {
    if (onOrderUpdate) {
      onOrderUpdate(order, draggableItem)
    }
  }, [order])
  useEffect(() => {
    if (isOver && draggableItem.sortableId !== id) {
      overIndex.current = order.length + 1

      if (!order.includes(NEW_SORTABLE_ITEM_KEY)) {
        setOrder((order: string[]) => {
          return [...order, NEW_SORTABLE_ITEM_KEY]
        })
      }
    }

    if (!isOver && overIndex.current !== null) {
      if (!didDrop) overIndex.current = null

      if (order.includes(NEW_SORTABLE_ITEM_KEY)) {
        setOrder((order: string[]) => {
          return order.filter(key => key !== NEW_SORTABLE_ITEM_KEY)
        })
      }
    }
  }, [draggableItem, isOver, didDrop])
  const handleDragOver = useCallback(
    (item, index) => {
      // seems better to pass actual item to SortableItem instead of just index
      // to avoid error-prone search of item by index in changing data
      overIndex.current = index
      setOrder((order: string[]) => {
        let state = [...order]

        if (
          item.sortableKey === NEW_SORTABLE_ITEM_KEY &&
          !state.includes(NEW_SORTABLE_ITEM_KEY)
        ) {
          state.splice(index, 0, NEW_SORTABLE_ITEM_KEY)
        }

        let fromIndex: number = state.indexOf(item.sortableKey)

        if (item.sortableId !== id) {
          fromIndex = state.indexOf(NEW_SORTABLE_ITEM_KEY)
        }

        if (
          item.sortableId === id &&
          item.isPartOfMultipleSelection &&
          Array.isArray(item.selection)
        ) {
          const hiddenItems: string[] = []

          for (const index of item.selection) {
            const key = itemsKeys[index]

            if (key && key !== item.sortableKey) {
              hiddenItems.push(key)
            }
          }

          state = state.filter((key: string) => !hiddenItems.includes(key))
        }

        return moveItems2(state, fromIndex, index)
      })
    },
    [id, itemsKeys, keyExtractor, items, order],
  )
  const handleDropOutside = useCallback(() => {
    // Reset order when drag was cancelled or dropped to other target
    setOrder(itemsKeys)
  }, [itemsKeys])
  const sortedItems = order
    .filter((key: string) => key !== NEW_SORTABLE_ITEM_KEY)
    .map((key: string) => itemsMap.get(key))

  const renderPreview = () =>
    hidePreview ? null : (
      <div className="Draggable Draggable--hover">
        <div
          style={{
            height: draggableItem?.height,
            opacity: 0,
          }}
        >
          {draggableItem?.files && FILE_PREVIEW}
        </div>
      </div>
    )

  const renderItems = () =>
    order.map((key: string, index: number) => {
      const dragoverItemIndex = items.findIndex(
        item => keyExtractor(item, -1) === order[index],
      )
      const dragoverItem = items[dragoverItemIndex]
      return (
        <SortableContext.Provider
          key={key}
          value={{
            sortableId: id,
            sortableKey: key,
            onDropOutside: handleDropOutside,
          }}
        >
          <SortableItem
            accept={accept}
            index={index}
            onDragOver={
              overIndex.current === index ? undefined : handleDragOver
            }
            dragoverItem={dragoverItem}
            dragoverItemIndex={dragoverItemIndex}
            thresholdHandler={thresholdHandler}
          >
            {key === NEW_SORTABLE_ITEM_KEY
              ? renderPreview()
              : renderItem(itemsMap.get(key), index, sortedItems)}
          </SortableItem>
        </SortableContext.Provider>
      )
    })

  return (
    // @ts-expect-error
    <div ref={drop(containerRef)}>
      {typeof children === 'function' ? children(renderItems()) : renderItems()}
    </div>
  )
}

export default React.memo<Props>(Sortable)
